import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { formatCurrency, getDateRange, formatDateOnlyDisplay } from '../utils/helpers';

const Report = ({ onOpenVehicleDetail, onDisplayInvoice }) => {
  const { vehicles, invoices } = useApp();
  const [period, setPeriod] = useState('week');
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedYearFilter, setSelectedYearFilter] = useState(null);
  const monthDropdownRef = useRef(null);
  const yearDropdownRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i); // Last 5 years
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target)) {
        setShowMonthDropdown(false);
      }
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target)) {
        setShowYearDropdown(false);
      }
    };

    if (showMonthDropdown || showYearDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMonthDropdown, showYearDropdown]);

  const periodLabels = {
    day: 'Today',
    week: 'Last 7 Days',
    month: selectedMonth !== null && selectedYear !== null 
      ? new Date(selectedYear, selectedMonth).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      : 'Last 30 Days',
    year: selectedYearFilter !== null ? selectedYearFilter.toString() : 'This Year'
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'month') {
      setSelectedMonth(null);
      setSelectedYear(null);
      setShowMonthDropdown(false);
    }
    if (newPeriod !== 'year') {
      setSelectedYearFilter(null);
      setShowYearDropdown(false);
    }
  };

  const handleMonthClick = () => {
    if (period === 'month') {
      setShowMonthDropdown(!showMonthDropdown);
    } else {
      handlePeriodChange('month');
      setShowMonthDropdown(true);
    }
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    if (month !== null && selectedYear === null) {
      setSelectedYear(currentYear);
    }
    setShowMonthDropdown(false);
  };

  const handleYearClick = () => {
    if (period === 'year') {
      setShowYearDropdown(!showYearDropdown);
    } else {
      handlePeriodChange('year');
      setShowYearDropdown(true);
    }
  };

  const handleYearSelect = (year) => {
    setSelectedYearFilter(year);
    setShowYearDropdown(false);
  };

  const reportData = useMemo(() => {
    const { startDate, endDate } = getDateRange(period, selectedMonth, selectedYear, selectedYearFilter);
    
    // DEBUG: Log date range
    const today = new Date();
    console.log(`[Report Debug] Period: ${period}`);
    console.log(`[Report Debug] Today:`, today.toLocaleDateString('en-GB'), `(Day of week: ${today.getDay()}, 0=Sun, 1=Mon, etc.)`);
    console.log(`[Report Debug] Start Date:`, startDate.toISOString(), startDate.toLocaleDateString('en-GB'));
    console.log(`[Report Debug] End Date:`, endDate.toISOString(), endDate.toLocaleDateString('en-GB'));
    console.log(`[Report Debug] Start Time:`, startDate.getTime());
    console.log(`[Report Debug] End Time:`, endDate.getTime());
    
    // Helper function to normalize a date to local midnight
    // Handles both ISO strings (UTC) and local date strings
    const normalizeToLocalMidnight = (dateInput) => {
      if (!dateInput) return null;
      
      let date;
      if (typeof dateInput === 'string') {
        // If it's an ISO string, extract the date part before converting
        // This avoids timezone issues when the UTC time converts to a different local day
        if (dateInput.includes('T')) {
          const datePart = dateInput.split('T')[0]; // Get "YYYY-MM-DD" part
          const [year, month, day] = datePart.split('-').map(Number);
          // Create date directly in local timezone to avoid timezone conversion issues
          date = new Date(year, month - 1, day, 0, 0, 0, 0);
        } else {
          // For non-ISO strings, parse normally
          date = new Date(dateInput);
          if (!isNaN(date.getTime())) {
            // Normalize to local midnight
            date = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
          }
        }
      } else if (dateInput instanceof Date) {
        // Already a Date object, normalize to local midnight
        date = new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate(), 0, 0, 0, 0);
      } else {
        date = new Date(dateInput);
        if (!isNaN(date.getTime())) {
          date = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
        }
      }
      
      if (!date || isNaN(date.getTime())) return null;
      return date;
    };
    
    const invoiceMap = new Map();
    invoices.forEach(inv => {
      if (!invoiceMap.has(inv.reg)) {
        invoiceMap.set(inv.reg, []);
      }
      invoiceMap.get(inv.reg).push(inv);
    });

    // DEBUG: Log invoices
    console.log(`[Report Debug] Total invoices:`, invoices.length);
    if (invoices.length > 0) {
      console.log(`[Report Debug] Sample invoice dates:`, invoices.slice(0, 5).map(inv => ({
        reg: inv.reg,
        date: inv.date,
        dateType: typeof inv.date,
        parsedDate: new Date(inv.date).toLocaleDateString('en-GB')
      })));
    }

    // DEBUG: Log paid vehicles
    const allPaidVehicles = vehicles.filter(veh => veh.paid === true && !veh.deletedAt);
    console.log(`[Report Debug] Total paid vehicles:`, allPaidVehicles.length);

    const paidVehicles = allPaidVehicles.map(veh => {
      let paymentDate = null;
      let dateSource = 'unknown';
      
      if (invoiceMap.has(veh.reg)) {
        const vehInvoices = invoiceMap.get(veh.reg);
        const latestInvoice = vehInvoices.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        paymentDate = normalizeToLocalMidnight(latestInvoice.date);
        dateSource = `invoice: ${latestInvoice.date}`;
      } else if (veh.collectionDate) {
        paymentDate = normalizeToLocalMidnight(veh.collectionDate);
        dateSource = `collectionDate: ${veh.collectionDate}`;
      } else {
        paymentDate = veh.dropOffDate ? normalizeToLocalMidnight(veh.dropOffDate) : normalizeToLocalMidnight(new Date());
        dateSource = veh.dropOffDate ? `dropOffDate: ${veh.dropOffDate}` : 'new Date()';
      }
      
      // DEBUG: Log payment date extraction
      if (allPaidVehicles.length <= 10) { // Only log if not too many vehicles
        console.log(`[Report Debug] Vehicle ${veh.reg}:`, {
          dateSource,
          rawDate: invoiceMap.has(veh.reg) 
            ? invoiceMap.get(veh.reg).sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date
            : veh.collectionDate || veh.dropOffDate,
          normalizedDate: paymentDate ? paymentDate.toISOString() : 'null',
          normalizedLocalDate: paymentDate ? paymentDate.toLocaleDateString('en-GB') : 'null',
          normalizedTime: paymentDate ? paymentDate.getTime() : 'null'
        });
      }
      
      return {
        vehicle: veh,
        paymentDate: paymentDate
      };
    });

    const startTime = startDate.getTime();
    const endTime = endDate.getTime();
    
    // DEBUG: Log all payment dates with detailed info
    const paymentDatesDebug = paidVehicles.slice(0, 10).map(({ vehicle, paymentDate }) => ({
      reg: vehicle.reg,
      paymentDate: paymentDate ? paymentDate.toLocaleDateString('en-GB') : 'null',
      paymentDateISO: paymentDate ? paymentDate.toISOString() : 'null',
      paymentTime: paymentDate ? paymentDate.getTime() : 'null',
      hasInvoice: invoiceMap.has(vehicle.reg),
      collectionDate: vehicle.collectionDate,
      dropOffDate: vehicle.dropOffDate,
      invoiceDate: invoiceMap.has(vehicle.reg) 
        ? invoiceMap.get(vehicle.reg).sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date
        : null
    }));
    console.log(`[Report Debug] All payment dates (first 10):`, paymentDatesDebug);
    console.table(paymentDatesDebug); // Easier to read in table format
    
    // DEBUG: Show date range comparison
    const inRange = paidVehicles.filter(({ paymentDate }) => {
      if (!paymentDate || isNaN(paymentDate.getTime())) return false;
      const paymentTime = paymentDate.getTime();
      return paymentTime >= startTime && paymentTime <= endTime;
    });
    const before = paidVehicles.filter(({ paymentDate }) => {
      if (!paymentDate || isNaN(paymentDate.getTime())) return false;
      return paymentDate.getTime() < startTime;
    });
    const after = paidVehicles.filter(({ paymentDate }) => {
      if (!paymentDate || isNaN(paymentDate.getTime())) return false;
      return paymentDate.getTime() > endTime;
    });
    
    console.log(`[Report Debug] Date Range Check:`, {
      period,
      startDate: startDate.toLocaleDateString('en-GB'),
      endDate: endDate.toLocaleDateString('en-GB'),
      startTime,
      endTime,
      paymentDatesInRange: inRange.length,
      paymentDatesBefore: before.length,
      paymentDatesAfter: after.length
    });
    
    // Show sample dates that are before/after
    if (before.length > 0) {
      console.log(`[Report Debug] Sample dates BEFORE range (first 3):`, before.slice(0, 3).map(({ vehicle, paymentDate }) => ({
        reg: vehicle.reg,
        date: paymentDate.toLocaleDateString('en-GB'),
        time: paymentDate.getTime()
      })));
    }
    if (after.length > 0) {
      console.log(`[Report Debug] Sample dates AFTER range (first 3):`, after.slice(0, 3).map(({ vehicle, paymentDate }) => ({
        reg: vehicle.reg,
        date: paymentDate.toLocaleDateString('en-GB'),
        time: paymentDate.getTime()
      })));
    }
    if (inRange.length > 0) {
      console.log(`[Report Debug] Sample dates IN range (first 3):`, inRange.slice(0, 3).map(({ vehicle, paymentDate }) => ({
        reg: vehicle.reg,
        date: paymentDate.toLocaleDateString('en-GB'),
        time: paymentDate.getTime()
      })));
    }
    
    let comparisonCount = 0;
    const periodVehicles = paidVehicles.filter(({ paymentDate, vehicle }) => {
      if (!paymentDate || isNaN(paymentDate.getTime())) {
        console.log(`[Report Debug] Filtered out ${vehicle.reg}: invalid payment date`);
        return false;
      }
      // Compare dates using getTime() for accurate comparison
      const paymentTime = paymentDate.getTime();
      const isInRange = paymentTime >= startTime && paymentTime <= endTime;
      
      // DEBUG: Log comparison for first 5 vehicles
      if (comparisonCount < 5) {
        console.log(`[Report Debug] Comparing ${vehicle.reg}:`, {
          paymentDate: paymentDate.toLocaleDateString('en-GB'),
          paymentDateISO: paymentDate.toISOString(),
          paymentTime,
          startDate: startDate.toLocaleDateString('en-GB'),
          startTime,
          endDate: endDate.toLocaleDateString('en-GB'),
          endTime,
          isInRange,
          reason: !isInRange ? (paymentTime < startTime ? `before start (${paymentTime} < ${startTime})` : `after end (${paymentTime} > ${endTime})`) : 'in range'
        });
        comparisonCount++;
      }
      
      return isInRange;
    });

    // DEBUG: Log filtered results
    console.log(`[Report Debug] Vehicles in period:`, periodVehicles.length);
    console.log(`[Report Debug] Filtered vehicle regs:`, periodVehicles.map(({ vehicle }) => vehicle.reg));

    const vehicleEarnings = periodVehicles.map(({ vehicle: veh, paymentDate }) => {
      const subtotal = veh.cost || 0;
      const vatEnabled = veh.status === 'MOT' ? false : (veh.includeVat !== undefined ? veh.includeVat : true);
      const vat = vatEnabled ? subtotal * 0.2 : 0;
      const total = subtotal + vat;
      return {
        reg: veh.reg,
        vehicle: veh.vehicle,
        amount: total,
        date: paymentDate.toISOString(),
        vehicleData: veh
      };
    });

    const totalEarnings = vehicleEarnings.reduce((sum, v) => sum + v.amount, 0);
    const totalVehicles = vehicleEarnings.length;

    return {
      totalEarnings,
      totalVehicles,
      vehicleEarnings: vehicleEarnings.sort((a, b) => b.amount - a.amount)
    };
  }, [vehicles, invoices, period, selectedMonth, selectedYear, selectedYearFilter]);

  const handleVehicleClick = (item) => {
    const vehicleInvoices = invoices.filter(inv => inv.reg === item.reg);
    
    if (vehicleInvoices.length > 0 && onDisplayInvoice) {
      // Display the latest invoice if it exists
      const latestInvoice = vehicleInvoices.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      onDisplayInvoice(latestInvoice);
    } else if (onDisplayInvoice) {
      // Create a temporary invoice from vehicle data if no invoice exists
      const veh = vehicles.find(v => v.reg === item.reg);
      if (veh) {
        const subtotal = veh.cost || 0;
        const vatEnabled = veh.status === 'MOT' ? false : (veh.includeVat !== undefined ? veh.includeVat : true);
        const vat = vatEnabled ? subtotal * 0.2 : 0;
        const total = subtotal + vat;
        const tempInvoice = {
          number: `TEMP-${veh.reg}`,
          date: item.date,
          reg: veh.reg,
          vehicle: veh.vehicle,
          contact: veh.contact || '',
          summary: veh.summary || '',
          motStatus: veh.motStatus || '',
          dropOffDate: veh.dropOffDate 
            ? (formatDateOnlyDisplay(veh.dropOffDate, { day: 'numeric', month: 'long', year: 'numeric' }) || 'Not set')
            : 'Not set',
          collectionDate: veh.collectionDate 
            ? (formatDateOnlyDisplay(veh.collectionDate, { day: 'numeric', month: 'long', year: 'numeric' }) || 'Not set')
            : 'Not set',
          subtotal: subtotal,
          vat: vat,
          total: total
        };
        onDisplayInvoice(tempInvoice);
      }
    } else if (onOpenVehicleDetail) {
      // Fallback to opening vehicle detail if no invoice handler
      const vehicleIndex = vehicles.findIndex(v => v.reg === item.reg);
      if (vehicleIndex !== -1) {
        onOpenVehicleDetail(vehicleIndex);
      }
    }
  };

  return (
    <div className="vehicle-inner report-page">
      <div className="vehicle-header">
        <div>
          <h2>Report</h2>
          <p className="vehicle-subtitle">Financial overview and vehicle earnings</p>
        </div>
      </div>
      <div className="report-container">
        <div className="report-filters" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', position: 'relative' }}>
          <button 
            type="button" 
            className={`report-filter-btn ${period === 'day' ? 'active' : ''}`}
            onClick={() => {
              handlePeriodChange('day');
              setShowMonthDropdown(false);
              setShowYearDropdown(false);
            }}
          >
            Day
          </button>
          <button 
            type="button" 
            className={`report-filter-btn ${period === 'week' ? 'active' : ''}`}
            onClick={() => {
              handlePeriodChange('week');
              setShowMonthDropdown(false);
              setShowYearDropdown(false);
            }}
          >
            Week
          </button>
          <div style={{ position: 'relative' }} ref={monthDropdownRef}>
            <button 
              type="button" 
              className={`report-filter-btn ${period === 'month' ? 'active' : ''}`}
              onClick={handleMonthClick}
            >
              Month
            </button>
            {showMonthDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '8px',
                background: 'var(--card-bg, #fff)',
                border: '1px solid var(--border, #e5e7eb)',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                zIndex: 1000,
                minWidth: '200px'
              }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '12px', 
                    fontWeight: 600, 
                    color: 'var(--text-muted, #6b7280)',
                    marginBottom: '6px'
                  }}>
                    Month
                  </label>
                  <select
                    value={selectedMonth !== null ? selectedMonth : ''}
                    onChange={(e) => {
                      const monthIndex = e.target.value !== '' ? parseInt(e.target.value) : null;
                      handleMonthSelect(monthIndex);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border, #e5e7eb)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'var(--light-bg, #f9fafb)',
                      color: 'var(--text-dark, #1f2937)',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select Month</option>
                    {months.map((month, index) => (
                      <option key={index} value={index}>{month}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
          <div style={{ position: 'relative' }} ref={yearDropdownRef}>
            <button 
              type="button" 
              className={`report-filter-btn ${period === 'year' ? 'active' : ''}`}
              onClick={handleYearClick}
            >
              Year
            </button>
            {showYearDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '8px',
                background: 'var(--card-bg, #fff)',
                border: '1px solid var(--border, #e5e7eb)',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                zIndex: 1000,
                minWidth: '200px'
              }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '12px', 
                    fontWeight: 600, 
                    color: 'var(--text-muted, #6b7280)',
                    marginBottom: '6px'
                  }}>
                    Year
                  </label>
                  <select
                    value={selectedYearFilter !== null ? selectedYearFilter : ''}
                    onChange={(e) => {
                      const year = e.target.value !== '' ? parseInt(e.target.value) : null;
                      handleYearSelect(year);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border, #e5e7eb)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'var(--light-bg, #f9fafb)',
                      color: 'var(--text-dark, #1f2937)',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">This Year</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="report-summary" id="reportSummary">
          <div className="report-summary-item">
            <div className="report-summary-label">Period</div>
            <div className="report-summary-value" style={{ fontSize: '18px' }}>
              {periodLabels[period]}
            </div>
          </div>
          <div className="report-summary-item">
            <div className="report-summary-label">Total Vehicles</div>
            <div className="report-summary-value">{reportData.totalVehicles}</div>
          </div>
          <div className="report-summary-item">
            <div className="report-summary-label">Total Earnings</div>
            <div className="report-summary-value">{formatCurrency(reportData.totalEarnings)}</div>
          </div>
        </div>
        <div className="report-vehicles" id="reportVehicles">
          {reportData.vehicleEarnings.length === 0 ? (
            <div className="empty-state">No financial data for this period.</div>
          ) : (
            reportData.vehicleEarnings.map((item, idx) => {
              const date = new Date(item.date);
              return (
                <div
                  key={idx}
                  className="report-vehicle-card"
                  onClick={() => handleVehicleClick(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="report-vehicle-reg">{item.reg}</div>
                  <div className="report-vehicle-info">
                    <div className="report-vehicle-details">{item.vehicle}</div>
                    <div className="report-vehicle-details" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                  <div className="report-vehicle-amount">{formatCurrency(item.amount)}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Report;
